import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Query codes are bearer invitations: consume in memory, never router state/storage.
const InvitationLinkAccess: React.FC<React.PropsWithChildren<{ slug: string }>> = ({ slug, children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, loading, error } = useAuth();
  const queued = useRef<{ code: string; slug: string; generation: number } | null>(null);
  const generation = useRef(0);
  const mounted = useRef(true);
  const [state, setState] = useState({ pending: new URLSearchParams(location.search).has('code'), error: '' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('code')) return;
    const values = params.getAll('code');
    const code = values.length === 1 ? values[0].trim() : '';
    params.delete('code');
    // Replace, not push: don't retain the code in this tab's current history entry.
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
    const current = ++generation.current;
    const valid = /^(?:[a-zA-Z0-9]{8}|[a-fA-F0-9]{24})$/.test(code);
    queued.current = valid ? { code, slug, generation: current } : null;
    setState({ pending: valid, error: valid ? '' : 'This invitation link is invalid. Enter the code from your event organizer below.' });
    setAttempt(value => value + 1);
  }, [location.pathname, location.search, location.hash, navigate, slug]);

  useEffect(() => {
    if (loading || error || !session || !queued.current) return;
    // Defer consumption so StrictMode's effect replay cannot redeem twice.
    void Promise.resolve().then(async () => {
      if (!mounted.current || !queued.current || queued.current.slug !== slug) return;
      const invitation = queued.current;
      queued.current = null;
      if (session.can_manage) { setState({ pending: false, error: '' }); return; }
      try {
        const { data } = await axios.post('/api/invitations/redeem', { code: invitation.code, slug });
        if (data?.slug !== slug) throw new Error('Mismatched invitation');
        if (mounted.current && invitation.generation === generation.current) setState({ pending: false, error: '' });
      } catch (failure) {
        if (!mounted.current || invitation.generation !== generation.current) return;
        const status = (failure as { response?: { status?: number } })?.response?.status;
        setState({ pending: false, error: status === 429
          ? 'Too many invitation attempts. Please wait a few minutes and enter your code below.'
          : status && [400, 401, 403, 404].includes(status)
            ? 'This invitation link is invalid or expired. Ask your event organizer for the current code.'
            : 'Unable to open this invitation link. Please enter your code below to try again.' });
      }
    });
  }, [attempt, loading, error, session, slug]);

  if (state.pending && !error) return <Box role="status" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
    <CircularProgress size={22} /><Typography>Opening invitation…</Typography>
  </Box>;
  return <>{state.error && <Alert severity="warning" sx={{ mb: 2 }}>{state.error}</Alert>}{children}</>;
};

export default InvitationLinkAccess;
